<template>
  <div class="space-y-6">
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <i class="pi pi-spin pi-spinner text-4xl text-gray-400" />
    </div>
    <div
      v-else-if="!(user || profileId)"
      class="flex items-center justify-center py-12"
    >
      <p class="text-gray-500">No player data available</p>
    </div>
    <div v-else class="space-y-6">
      <div class="flex justify-start gap-3 mb-4">
        <div class="relative">
          <i
            class="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <InputText
            v-model="searchQuery"
            placeholder="Search fields..."
            class="w-full pl-10"
            data-testid="search-input"
          />
        </div>
        <Select
          v-model="selectedSection"
          data-testid="section-select"
          :options="sectionOptions"
          option-label="label"
          option-value="value"
          class="w-32"
        />
        <span class="self-end ml-auto">
          <Button
            v-if="isEditingAll"
            label="Cancel"
            severity="link"
            class="mr-2 text-slate-500 font-medium"
            data-testid="cancel-button"
            @click="cancelClickHandler"
          />
          <Button
            :label="isEditingAll ? 'Save' : 'Edit'"
            :icon="buttonIcon"
            :disabled="shouldButtonBeDisabled"
            :class="`${buttonColor} border-none text-white px-4 py-2 rounded-lg`"
            data-testid="edit-save-button"
            @click="editClickHandler"
          />
        </span>
      </div>

      <!-- Player Info Section -->
      <PlayerInfoSection
        v-if="shouldShowSection('player')"
        v-model:form-rows="playerDataViewModel"
        :search-query="searchQuery"
        :is-editing="isEditingAll"
        @enter-edit-mode="isEditingAll = true"
      />

      <!-- Athletic Info Section -->
      <AthleticInfoSection
        v-if="shouldShowSection('athletic')"
        v-model:form-rows="athleticInfoRows"
        :search-query="searchQuery"
        :is-editing="isEditingAll"
        @enter-edit-mode="isEditingAll = true"
      />

      <!-- Health & Safety Section -->
      <HealthSafetySection
        v-if="shouldShowSection('health')"
        v-model:form-rows="healthSafetyRows"
        v-model:is-invalid="isHealthSafetyInvalid"
        :search-query="searchQuery"
        :is-editing="isEditingAll"
        @enter-edit-mode="isEditingAll = true"
      />

      <!-- Recruiting Info Section -->
      <RecruitingInfoSection
        v-if="shouldShowSection('recruiting')"
        v-model:form-rows="recruitingInfoRows"
        v-model:is-invalid="isRecruitingInfoInvalid"
        :search-query="searchQuery"
        :is-editing="isEditingAll"
        @enter-edit-mode="isEditingAll = true"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import {
  type AdminPlayerData,
  type AdminProfile,
  type PatchedAdminPlayerData,
} from "@/api/model";
import { useToast } from "primevue/usetoast";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import Button from "primevue/button";
import {
  usePlayerData,
  type PlayerDataAPI,
  type ErrorGroup,
  type NonReadOnlyAdminPlayerData,
} from "@/modules/profile/composables/usePlayerData";
import {
  CollapsableAthleticInfoPatchDTO,
  athleticInfoRequestModelToViewModel,
} from "@/modules/profile/models/AthleticInfo";
import {
  CollapsableHealthAndSafetyPatchDTO,
  healthAndSafetyRequestModelToViewModel,
} from "@/modules/profile/models/HealthAndSafety";
import {
  CollapsablePlayerDataPatchDTO,
  playerDataBaseModelRequestToViewModel,
} from "@/modules/profile/models/PlayerData";
import {
  CollapsableRecruitingInfoPatchDTO,
  recruitingInfoToViewModel,
} from "@/modules/profile/models/RecruitingInfo";
import { emptyOrBlank } from "@/modules/profile/models/emptyOrBlank";
import {
  type PlayerDataEvent,
  type ReadModelRecord,
} from "@/modules/profile/types";
import {
  PlayerInfoSection,
  AthleticInfoSection,
  HealthSafetySection,
  RecruitingInfoSection,
} from "@/modules/profile/components/sections";

export type { PlayerDataAPI };

const props = defineProps<{
  user?: AdminProfile | null;
  profileId?: number;
  organizationId?: number;
  playerDataApi?: PlayerDataAPI;
}>();

const emit = defineEmits<{
  playerDataChanged: [data: PlayerDataEvent];
  saving: [isSaving: boolean];
}>();

const organizationId = computed(
  () => props.user?.organization?.id || props.organizationId
);
const profileId = computed(() => props.user?.id || props.profileId);

const playerData = usePlayerData(props.playerDataApi);
const toast = useToast();
const hasData = ref<boolean | null>(null);
const isLoading = computed(
  () => playerData.isLoading.value && hasData.value !== false
);
const searchQuery = ref("");
const selectedSection = ref("all");
const isEditingAll = ref(false);
const isHealthSafetyInvalid = ref(false);
const isRecruitingInfoInvalid = ref(false);
const isInvalid = computed(
  () => isHealthSafetyInvalid.value || isRecruitingInfoInvalid.value
);

const sectionOptions = [
  { label: "All", value: "all" },
  { label: "Player Info", value: "player" },
  { label: "Athletic Info", value: "athletic" },
  { label: "Health & Safety", value: "health" },
  { label: "Recruiting Info", value: "recruiting" },
];

watch(playerData.fetchError, (_newValue, _oldValue) => {
  const errorResponse = playerData.fetchError.value as ErrorGroup | null;
  if (errorResponse) {
    if (errorResponse.status == 404 && errorResponse.data.errors.length == 1) {
      hasData.value = false;
    } else {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Failed to load player data",
        life: 3000,
      });
    }
  }
});

watch(playerData.updateError, (_newValue, _oldValue) => {
  if (playerData.updateError.value) {
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to save",
      life: 3000,
    });
  }
});

watch(
  () => props.user,
  (newUser) => {
    if (newUser) {
      loadPlayerData(newUser.id);
    }
  }
);
const save = async (
  playerDataPayload: PatchedAdminPlayerData | NonReadOnlyAdminPlayerData
) => {
  if (organizationId.value) {
    if (hasData.value && playerData.datum.value?.id) {
      return playerData.update(
        organizationId.value,
        playerData.datum.value.id,
        playerDataPayload
      );
    } else {
      return playerData.create(organizationId.value, playerDataPayload);
    }
  }
};

const editClickHandler = async () => {
  isEditingAll.value = !isEditingAll.value;

  if (!isEditingAll.value) {
    emit("saving", true);
    const playerDataPayload = mapToResponse();
    const result = await save(playerDataPayload);
    emit("saving", false);
    if (result) {
      hasData.value = true;
      lastSaveState = playerData.datum.value;
      toast.add({
        severity: "success",
        summary: "Success",
        detail: "Profile Updated Successfully",
        life: 3000,
      });
      emit("playerDataChanged", mapPlayerDataToEvent());
    }
  }
};

const mapPlayerDataToEvent = (): PlayerDataEvent => {
  const trimModelForEvent = (
    model:
      | typeof playerDataViewModel.value
      | typeof athleticInfoRows.value
      | typeof healthSafetyRows.value
      | typeof recruitingInfoRows.value
  ) => {
    return Object.entries(model).reduce(
      (acc, [key, record]) => {
        if (
          record.data instanceof Temporal.PlainDate ||
          !emptyOrBlank(record.data)
        ) {
          const { lastUpdated: _, ...rest } = record;
          acc[key] = rest;
        }
        return acc;
      },
      {} as Record<string, Omit<ReadModelRecord, "lastUpdated" | "display">>
    );
  };

  const filteredPlayerData = trimModelForEvent(playerDataViewModel.value);
  const filteredAthleticData = trimModelForEvent(athleticInfoRows.value);
  const filteredHealthSafetyData = trimModelForEvent(healthSafetyRows.value);
  const filteredRecruitingInfoData = trimModelForEvent(
    recruitingInfoRows.value
  );

  return {
    ...filteredPlayerData,
    healthAndSafety: filteredHealthSafetyData,
    athleticInfo: filteredAthleticData,
    recruitingInfo: filteredRecruitingInfoData,
  };
};

const loadPlayerData = (profileId: number) => {
  if (organizationId.value) {
    playerData.fetch(organizationId.value, profileId);
  }
};

const shouldShowSection = (section: string) => {
  return selectedSection.value === "all" || selectedSection.value === section;
};

const playerDataViewModel = ref(
  playerDataBaseModelRequestToViewModel(playerData.datum.value)
);

const athleticInfoRows = ref(
  athleticInfoRequestModelToViewModel(playerData.datum.value?.athletic_info)
);

const healthSafetyRows = ref(
  healthAndSafetyRequestModelToViewModel(
    playerData.datum.value?.health_and_safety
  )
);

const recruitingInfoRows = ref(
  recruitingInfoToViewModel(playerData.datum.value?.recruiting_info)
);

watch(playerData.datum, () => {
  playerDataViewModel.value = playerDataBaseModelRequestToViewModel(
    playerData.datum.value
  );
  athleticInfoRows.value = athleticInfoRequestModelToViewModel(
    playerData.datum.value?.athletic_info
  );
  healthSafetyRows.value = healthAndSafetyRequestModelToViewModel(
    playerData.datum.value?.health_and_safety
  );
  recruitingInfoRows.value = recruitingInfoToViewModel(
    playerData.datum.value?.recruiting_info
  );
});

let lastSaveState: AdminPlayerData | null = null;

watch(
  playerData.datum,
  (newValue, oldValue) => {
    if (oldValue === null && newValue !== null) {
      lastSaveState = newValue;
      hasData.value = true;
    }
    if (newValue) {
      emit("playerDataChanged", mapPlayerDataToEvent());
    }
  },
  { immediate: true }
);

const mapToResponse = ():
  | PatchedAdminPlayerData
  | NonReadOnlyAdminPlayerData => {
  const playerViewModel = playerDataViewModel.value;
  const dateOfBirth = playerViewModel.dateOfBirth.data;

  const healthViewModel = healthSafetyRows.value;
  const healthAndSafety = new CollapsableHealthAndSafetyPatchDTO({
    emergency_contact_1_name: healthViewModel.emergencyContact1Name.data,
    emergency_contact_1_email: healthViewModel.emergencyContact1Email.data,
    emergency_contact_1_phone: healthViewModel.emergencyContact1Phone.data,
    emergency_contact_2_name: healthViewModel.emergencyContact2Name.data,
    emergency_contact_2_email: healthViewModel.emergencyContact2Email.data,
    emergency_contact_2_phone: healthViewModel.emergencyContact2Phone.data,
    medical_conditions: healthViewModel.medicalConditions.data,
    allergies: healthViewModel.allergies.data,
    dietary_restrictions: healthViewModel.dietaryRestrictions.data,
    current_injuries: healthViewModel.currentInjuries.data,
  }).toJSON();

  const athleticViewModel = athleticInfoRows.value;
  const athleticInfo = new CollapsableAthleticInfoPatchDTO({
    primary_position_baseball: athleticViewModel.primaryPositionBaseball.data,
    secondary_position_baseball:
      athleticViewModel.secondaryPositionBaseball.data,
    primary_position_volleyball:
      athleticViewModel.primaryPositionVolleyball.data,
    secondary_position_volleyball:
      athleticViewModel.secondaryPositionVolleyball.data,
    handedness: athleticViewModel.handedness.data,
    throws: athleticViewModel.throws.data,
    bats: athleticViewModel.bats.data,
    skill_level: athleticViewModel.skillLevel.data,
    jersey_top_size: athleticViewModel.jerseyTopSize.data,
    jersey_bottom_size: athleticViewModel.jerseyBottomSize.data,
    first_choice_jersey_number: athleticViewModel.firstChoiceJerseyNumber.data,
    second_choice_jersey_number:
      athleticViewModel.secondChoiceJerseyNumber.data,
    hat_size: athleticViewModel.hatSize.data,
    hat_size_numbered: athleticViewModel.hatSizeNumbered.data,
  }).toJSON();

  const recruitingViewModel = recruitingInfoRows.value;
  const recruitingInfo = new CollapsableRecruitingInfoPatchDTO({
    gpa: recruitingViewModel.gpa.data,
    sat_score: recruitingViewModel.satScore.data,
    act_score: recruitingViewModel.actScore.data,
    ncaa_eligibility_center_id:
      recruitingViewModel.ncaaEligibilityCenterId.data,
    college_commitment: recruitingViewModel.collegeCommitment.data,
    highlights_recruiting_profile_link:
      recruitingViewModel.highlightsRecruitingProfileLink.data,
  }).toJSON();

  const playerDataPayload = new CollapsablePlayerDataPatchDTO({
    profile_id: profileId.value,
    birth_year: dateOfBirth?.year ?? null,
    birth_month: dateOfBirth?.month ?? null,
    birth_day: dateOfBirth?.day ?? null,
    gender: playerViewModel.gender.data,
    grade: playerViewModel.grade.data as PatchedAdminPlayerData["grade"],
    graduation_year: playerViewModel.graduationYear.data,
    college_graduation_year: playerViewModel.collegeGraduationYear.data,
    height_magnitude: playerViewModel.heightMagnitude.data,
    height_unit: playerViewModel.heightUnit.data,
    weight_magnitude: playerViewModel.weightMagnitude.data,
    weight_unit: playerViewModel.weightUnit.data,
    home_address: playerViewModel.homeAddress.data,
    current_school: playerViewModel.currentSchool.data,
    previous_teams: playerViewModel.previousTeams.data,
    twitter_handle: playerViewModel.twitterHandle.data,
    instagram_handle: playerViewModel.instagramHandle.data,
    athletic_info: athleticInfo,
    health_and_safety: healthAndSafety,
    recruiting_info: recruitingInfo,
  }).toJSON() as PatchedAdminPlayerData;

  return playerDataPayload;
};

const resetFormFields = <T extends Record<string, any>>(rows: T) => {
  Object.keys(rows).forEach((key) => {
    const field = rows[key as keyof T];
    if (field.hasOwnProperty("values")) {
      field.data = "";
    } else {
      field.data = undefined;
    }
  });
};

const cancelClickHandler = () => {
  if (lastSaveState) {
    playerData.datum.value = JSON.parse(JSON.stringify(lastSaveState));
  } else {
    resetFormFields(playerDataViewModel.value);
    resetFormFields(athleticInfoRows.value);
    resetFormFields(healthSafetyRows.value);
    resetFormFields(recruitingInfoRows.value);
  }
  isEditingAll.value = false;
};

const buttonColor = computed(() => {
  if (isInvalid.value) {
    return "bg-red-500";
  } else if (isEditingAll.value) {
    return "bg-primary";
  } else {
    return "bg-slate-500";
  }
});

const buttonIcon = computed(() => {
  if (playerData.isSaving.value) {
    return "pi pi-spin pi-spinner";
  } else if (isEditingAll.value) {
    return "";
  } else {
    return "pi pi-pencil";
  }
});

const shouldButtonBeDisabled = computed((): boolean => {
  return isInvalid.value || (playerData.isSaving.value as boolean);
});

onMounted(() => {
  if (profileId.value) {
    loadPlayerData(profileId.value);
  }
});
</script>
